import type { RolePermissions } from './rolesType';

export type PermissionsType = string;

export const rolesData = {
  permissions: ['add_role', 'change_role', 'delete_role', 'view_role'],
};
export const rolePermissions = (rolesData?.permissions ?? []) as Permission[];

// Permissions
export type Permissions = RolePermissions;
export type Permission = Permissions[number];
export type PermissionsPartial = Partial<Permission[]>;
export type PermissionsRecord = Record<Permission, any>;
export type PermissionsPartialRecord = Partial<Record<Permission, any>>;

// Permissions List
export const permissionsLabels = {
  // ROLE
  add_role: 'Add Role',
  change_role: 'Change Role',
  delete_role: 'Delete Role',
  view_role: 'View Role',

  // USER
  add_user: 'Add User',
  change_user: 'Change User',
  delete_user: 'Delete User',
  view_user: 'View User',

  // PROJECT
  add_project: 'Add Project',
  change_project: 'Change Project',
  delete_project: 'Delete Project',
  view_project: 'View Project',
  manage_project_access: 'Manage Project Access',

  // RELEASE
  add_release: 'Add Release',
  change_release: 'Change Release',
  delete_release: 'Delete Release',
  view_release: 'View Release',

  // PRODUCTION SUMMARY
  view_productionsummary: 'View Frame Production Summary',

  // INVENTORY
  add_inventory: 'Add Inventory',
  change_inventory: 'Change Inventory',
  delete_inventory: 'Delete Inventory',
  view_inventory: 'View Inventory',

  // INVENTORY DISPATCH
  add_inventorydispatch: 'Add Site Delivery',
  change_inventorydispatch: 'Change Site Delivery',
  delete_inventorydispatch: 'Delete Site Delivery',
  view_inventorydispatch: 'View Site Delivery',
  post_inventorydispatch: 'Send Site Delivery',
  void_inventorydispatch: 'Reverse Site Delivery',

  // RFILog
  add_rfilog: 'Add RFILog',
  change_rfilog: 'Change RFILog',
  delete_rfilog: 'Delete RFILog',
  view_rfilog: 'View RFILog',

  // SUBMITTAL
  add_submittal: 'Add Submittal',
  change_submittal: 'Change Submittal',
  delete_submittal: 'Delete Submittal',
  view_submittal: 'View Submittal',

  // WORKORDER
  add_workorder: 'Add Work Order',
  change_workorder: 'Change Work Order',
  delete_workorder: 'Delete Work Order',
  view_workorder: 'View Work Order',
  override_workorder_assignment:
    'Override Work Order Assignment (Supervisor Completion)',

  // PRODUCTION CHECKLIST
  view_checklist: 'View Production Checklist',
  change_checklist: 'Manage Production Checklists',

  // PACKING
  add_packingslip: 'Add Packing Slip',
  change_packingslip: 'Change Packing Slip',
  delete_packingslip: 'Delete Packing Slip',
  view_packingslip: 'View Packing Slip',

  // SHIPPING
  add_shippinglist: 'Add Shipping List',
  change_shippinglist: 'Change Shipping List',
  delete_shippinglist: 'Delete Shipping List',
  view_shippinglist: 'View Shipping List',

  // SUPPLIER
  add_supplier: 'Add Supplier',
  change_supplier: 'Change Supplier',
  delete_supplier: 'Delete Supplier',
  view_supplier: 'View Supplier',

  // PURCHASE ORDER
  add_purchaseorder: 'Add Purchase Order',
  change_purchaseorder: 'Change Purchase Order',
  delete_purchaseorder: 'Delete Purchase Order',
  view_purchaseorder: 'View Purchase Order',

  // QUALITY CONTROL
  add_qualityevent: 'Add Quality Event',
  change_qualityevent: 'Change Quality Event',
  delete_qualityevent: 'Delete Quality Event',
  view_qualityevent: 'View Quality Event',

  // MASTER FILES
  delete_framemasterfile: 'Delete Frame Master File',
  delete_partmasterfile: 'Delete Part Master File',

  // FABRICATION MACHINE
  add_fabricationmachine: 'Add Fabrication Machine',
  change_fabricationmachine: 'Change Fabrication Machine',
  delete_fabricationmachine: 'Delete Fabrication Machine',
  view_fabricationmachine: 'View Fabrication Machine',

  // FABRICATION PROCESS
  add_fabricationprocess: 'Add Fabrication Process',
  change_fabricationprocess: 'Change Fabrication Process',
  delete_fabricationprocess: 'Delete Fabrication Process',
  view_fabricationprocess: 'View Fabrication Process',

  // FABRICATION PART CONFIG
  change_fabricationpartconfig: 'Change Fabrication Part Config',
  view_fabricationpartconfig: 'View Fabrication Part Config',

  // FABRICATION WORK ORDER
  add_fabricationworkorder: 'Add Fabrication Work Order',
  change_fabricationworkorder: 'Change Fabrication Work Order',
  delete_fabricationworkorder: 'Delete Fabrication Work Order',
  view_fabricationworkorder: 'View Fabrication Work Order',

  // FABRICATION EXECUTION LOG
  add_fabricationexecutionlog: 'Add Fabrication Execution Log',
  change_fabricationexecutionlog: 'Change Fabrication Execution Log',
  view_fabricationexecutionlog: 'View Fabrication Execution Log',

  // FABRICATION INVENTORY MOVEMENT
  view_fabricationinventorymovement: 'View Fabrication Inventory Movement',

  // PROJECT BUDGET
  add_projectbudget: 'Add Project Budget',
  change_projectbudget: 'Change Project Budget',
  delete_projectbudget: 'Delete Project Budget',
  view_projectbudget: 'View Project Budget',
  add_projectbudgetline: 'Add Budget Line',
  change_projectbudgetline: 'Change Budget Line',
  delete_projectbudgetline: 'Delete Budget Line',
  view_projectbudgetline: 'View Budget Line',
  import_projectbudget: 'Import Project Budget',
  apply_changeorder_to_budget: 'Apply Change Order to Budget',
  create_projectbudget_snapshot: 'Create Project Budget Snapshot',
  view_projectbudget_statistics: 'View Project Budget Statistics',
  override_co_allocation_limit: 'Override Change Order Allocation Limit',

  // TRACKING & LABELS
  view_trackeditem: 'View Tracked Items',
  add_trackeditem: 'Register Tracked Items',
  change_trackeditem: 'Edit Tracked Items',
  print_trackeditem: 'Print Tracking Labels and Reports',
  link_trackeditem: 'Link Tracked Items',
  merge_trackeditem: 'Merge Duplicate Tracked Items',
  void_trackeditem: 'Void Tracked Items',
  access_shop_trackeditem: 'Access Shop-Scope Tracked Items',
  manage_detached_trackeditem: 'Access Detached Tracked Items',
  reconcile_frameunit: 'Resolve Frame Unit Reconciliations',
  manage_shared_labeltemplate: 'Manage Shared Label Templates',
  manage_shared_printerprofile: 'Manage Shared Printer Profiles',
} as const;

export const permissionsList = Object.keys(permissionsLabels);

export const permissions = rolePermissions;

// get permission keys
// eg: { add_role: 'add_role' , change_role: 'change_role' }
// where the key of permissionsLabels is the permission required in the backend permissions
export const permissionsKeys = Object.fromEntries(
  Object.keys(permissionsLabels).map((key) => [key, key]),
) as { [K in keyof typeof permissionsLabels]: K };

export type PermissionsListingType = {
  description: (typeof permissionsLabels)[keyof typeof permissionsLabels];
  pKey: Permission;
};

export const PermissionsListing: PermissionsListingType[] = Object.keys(
  permissionsLabels,
).map((pKey) => ({
  description: permissionsLabels[pKey as keyof typeof permissionsLabels],
  pKey: pKey as keyof typeof permissionsLabels,
}));

export type PermissionsListingsType = typeof PermissionsListing;
export type PermissionsListing = PermissionsListingsType[number];
export type PermissionsListingPartial = Partial<PermissionsListing>;
export type PermissionsListingRecord = Record<Permission, any>;
export type PermissionsListingPartialRecord = Partial<Record<Permission, any>>;
